import { ArrowLeft, ArrowRight, Calendar, Clock } from 'lucide-react';

interface Post {
  slug: string;
  issue: number;
  date: string;
  readTime: string;
  title: string;
  subtitle: string;
  content: React.ReactNode;
}

const CTA = () => (
  <div className="bg-gradient-to-br from-rose-50 to-amber-50 border border-rose-100 rounded-2xl p-6 mt-10">
    <p className="font-bold text-gray-900 mb-2">Ready to become a FAM Funder?</p>
    <p className="text-gray-600 text-sm mb-4">Browse active campaigns and make your first contribution — starting at $5.</p>
    <div className="flex gap-3 flex-wrap">
      <a href="/campaigns" className="inline-flex items-center gap-1.5 bg-gradient-to-r from-rose-500 to-amber-500 text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:opacity-90 transition-opacity">
        Browse Campaigns <ArrowRight className="w-4 h-4" />
      </a>
      <a href="/funder" className="inline-flex items-center gap-1.5 border border-gray-300 text-gray-700 text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-gray-50 transition-colors">
        Learn how FAM works
      </a>
    </div>
  </div>
);

const POST_1: Post = {
  slug: 'own-the-paper-around-you',
  issue: 1,
  date: 'March 2026',
  readTime: '4 min read',
  title: 'Own the Paper Around You',
  subtitle: "Most people don't realize they're already surrounded by investment opportunities. FAM puts fractional vehicle ownership within reach — starting with the people you already know.",
  content: (
    <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
      <p>
        You probably know someone who needs a car right now. A cousin stretching a paycheck to cover repairs. A coworker who lost their vehicle in a tough month. A sibling getting back on their feet. The car is the thing holding the rest of their life together — and they can't quite reach it on their own.
      </p>
      <p>
        Here's what most people don't realize: <strong>you can own a piece of that car. And earn from it.</strong>
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10">What "owning the paper" means</h2>
      <p>
        In finance, "the paper" refers to the note — the financing instrument behind an asset. When a bank lends money for a car, the bank holds the paper. They earn interest. They profit from the vehicle's value over time.
      </p>
      <p>
        FAM flips this structure. Instead of a bank holding the paper alone, a community of funders — people who actually know the driver, or believe in the campaign — hold fractional shares in an LLC that owns the vehicle. You become the paper. You earn what the bank used to.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10">Why it works better than traditional lending</h2>
      <p>
        Traditional auto financing is adversarial by design. The bank doesn't know the borrower. The borrower doesn't know who's on the other end of their loan. Rates are set by risk models that have nothing to do with character.
      </p>
      <p>
        FAM is different because the funders often <em>do</em> know the person. They know their work ethic, their family, their situation. That context matters — and it means funders can support people they believe in while still building a real stake in something tangible.
      </p>
      <p>
        The creator pays into the LLC. Returns flow back proportionally. Over time, as the vehicle generates income — rideshare, deliveries, a work route — your fractional interest earns its keep.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10">The opportunity hiding in your social graph</h2>
      <p>
        Most investment platforms ask you to bet on strangers — companies you've never met, founders you've never shaken hands with, assets you'll never see. FAM inverts this. The most interesting investments are the ones right around you.
      </p>
      <p>
        Think about the people in your life who drive daily, who depend on their vehicles for income, who would be devastated without reliable transportation. These are your neighbors, your family, your community. The paper on their cars could belong to a faceless bank in another state — or it could belong to you.
      </p>
      <p>
        <strong>Own what's around you. Support people you believe in.</strong> That's FAM.
      </p>

      <CTA />
    </div>
  ),
};

const POST_2: Post = {
  slug: 'everyday-cars-better-investments',
  issue: 2,
  date: 'March 2026',
  readTime: '5 min read',
  title: 'Why Everyday Cars Are Better Investments Than You Think',
  subtitle: "Forget luxury assets. The most reliable vehicle investments are the ones people actually depend on every day.",
  content: (
    <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
      <p>
        The conventional wisdom says cars are bad investments. They depreciate. They break down. They cost money the moment you drive off the lot. That wisdom is correct — if you're the one buying the car for yourself.
      </p>
      <p>
        It does not apply if you're the one holding the paper.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10">Depreciation works differently when you're the lender</h2>
      <p>
        When you invest through FAM, you're not buying a car — you're buying a fractional stake in the financing structure around it. The vehicle depreciates. Your returns come from the payments made against it, not from reselling the vehicle at a premium.
      </p>
      <p>
        This is exactly how banks have made money on auto loans for decades. They don't care if the car loses value. They're collecting on the note. FAM puts that same position in the hands of community funders.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10">Why necessity beats luxury</h2>
      <p>
        High-end vehicle investments make headlines but carry enormous risk. A $200,000 exotic sits in a garage. It doesn't generate income. Its value is entirely tied to a volatile collector market.
      </p>
      <p>
        A 2020 Toyota Camry driven by someone who uses it to get to work, pick up their kids, and run deliveries on weekends is a different story. That vehicle is in active use. The person driving it has a strong, personal incentive to keep payments current — their livelihood depends on it. That's a quality of motivation no credit score fully captures.
      </p>
      <p>
        FAM is built around this insight. The campaigns on the platform aren't vanity purchases. They're transportation needs. The vehicles funded through FAM are the ones people depend on — which is precisely what makes them worth backing.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10">Cash flow from the cars you can actually see</h2>
      <p>
        There's something clarifying about investing in assets you can identify. You know the vehicle. You may know the driver. You understand what it's used for and why it matters. That's a level of due diligence no traditional fund gives you access to.
      </p>
      <p>
        FAM campaigns include the vehicle details, the creator's story, and the funding progress. You make an informed decision, not a statistical bet. And if you want to build a portfolio of stakes across multiple campaigns, the platform lets you do exactly that — spreading exposure across vehicles, creators, and timelines.
      </p>
      <p>
        The bank has been doing this in your neighborhood for years. FAM just opens the door.
      </p>

      <CTA />
    </div>
  ),
};

const POST_3: Post = {
  slug: 'power-fam-playbook',
  issue: 3,
  date: 'April 2026',
  readTime: '5 min read',
  title: 'Power FAM Playbook: How Groups Fund Together',
  subtitle: "A family, a friend crew, a community org — pooling contributions on one campaign is easier than you think. Here's how to do it right.",
  content: (
    <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
      <p>
        A Power FAM is what happens when a group decides to get behind one campaign together. A crew — people who know each other and know the campaign creator — pooling contributions with each member holding their own stake.
      </p>
      <p>
        It's the most social version of what FAM already does. And it's surprisingly straightforward to organize.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10">Who Power FAMs are for</h2>
      <p>
        Any group with a shared interest in getting someone a vehicle can be a Power FAM. Some examples from how these tend to form:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>A family that wants to get a parent or sibling into reliable transportation</li>
        <li>A friend group backing someone transitioning out of a tough situation</li>
        <li>A church, community org, or sports team rallying around one of their own</li>
        <li>A workplace crew supporting a colleague who lost their vehicle</li>
      </ul>
      <p>
        The defining characteristic isn't the relationship — it's the collective intent. A Power FAM means everyone in the group is consciously choosing to contribute, not just one person making a donation on behalf of others.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10">How to organize one</h2>
      <p>
        There's no special account type or application for a Power FAM. The campaign is the campaign — the creator launches it the same way as any other. What makes it a Power FAM is how the group coordinates around it.
      </p>
      <p>
        The practical playbook is simple: one person in the group (usually whoever is closest to the creator) shares the campaign link with everyone. Each member contributes individually — their own amount, on their own terms, as a gift or investment. The campaign page shows contributions accumulating. The creator sees momentum building from their community.
      </p>
      <p>
        There's no minimum group size. There's no application. If three people want to contribute to the same campaign together, that's a Power FAM.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10">What each member gets</h2>
      <p>
        Every contributing member of a Power FAM holds their own position. Gifts are gifts — they contribute to the funding total. Investments produce fractional LLC membership proportional to each member's contribution. Members don't share a single stake — each person's investment is their own.
      </p>
      <p>
        This matters for governance. After the vehicle is funded, investors can participate in management votes as individuals. A Power FAM group doesn't vote as a bloc unless they choose to coordinate that way on their own.
      </p>
      <p>
        The result is a funding event that feels collective — because it is — while remaining financially clean for everyone involved.
      </p>

      <CTA />
    </div>
  ),
};

const POST_4: Post = {
  slug: 'what-happens-after-campaign-funds',
  issue: 4,
  date: 'April 2026',
  readTime: '6 min read',
  title: 'What Happens After the Campaign Funds',
  subtitle: "You contributed. The campaign hit its goal. Now what? Here's the full picture — from FAM buying the vehicle to what your LLC stake actually means.",
  content: (
    <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
      <p>
        The campaign closes. The goal is met. Supporters and investors get a notification. And then — if you're new to FAM — you might wonder exactly what happens next. This is the full picture.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10">FAM buys the vehicle outright</h2>
      <p>
        FAM doesn't facilitate a loan in the traditional sense. When a campaign fully funds, FAM purchases the vehicle from the dealer directly — cash, in full. The dealer gets paid. The vehicle transfers. No bank is involved. No approval is pending.
      </p>
      <p>
        This is what makes the FAM model different from a co-signer arrangement or a peer lending platform. The vehicle changes hands on day one. The creator gets access to the vehicle via a retail contract. The funding community gets fractional ownership through the LLC that now holds the title.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10">The LLC and your membership certificate</h2>
      <p>
        Every funded vehicle gets its own LLC — formed off-platform through standard legal channels. As an investor, you receive a membership certificate by email. That document represents your fractional ownership interest in the LLC that holds the vehicle title.
      </p>
      <p>
        Your ownership percentage is proportional to your investment relative to the total campaign goal. A $1,000 investment in a $10,000 campaign is a 10% LLC stake. It's transparent, documented, and yours.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10">Governance: the LLC as a DAO</h2>
      <p>
        After funding, the LLC operates like a lightweight DAO. Any investor can initiate a management vote — on maintenance decisions, insurance, what happens if payments lapse, or eventual disposition of the vehicle. FAM holds a small algorithmic stake (never more than 10%) and participates in governance accordingly but cannot dominate votes.
      </p>
      <p>
        Votes are not frequent or mandatory. Most funded vehicles run smoothly with no intervention needed. But the mechanism is there — and it means investors have real recourse and real participation rights, not just a certificate on paper.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10">Payments and returns</h2>
      <p>
        The creator makes payments against their retail contract. Those payments flow through the LLC and are distributed to members proportionally. FAM manages the ledger — investors don't need to track or collect anything individually.
      </p>
      <p>
        When the vehicle is eventually sold or the creator's contract is fully satisfied, investors vote on how proceeds are distributed. The process closes cleanly, the LLC winds down, and each member receives their share.
      </p>
      <p>
        From contribution to close, the whole structure is designed to be hands-off for investors while remaining fully transparent at every stage.
      </p>

      <CTA />
    </div>
  ),
};

const POST_5: Post = {
  slug: 'why-dealers-love-fam',
  issue: 5,
  date: 'May 2026',
  readTime: '4 min read',
  title: 'Why Dealers Love FAM (Even If They Don\'t Know It Yet)',
  subtitle: "Standing offers, no-pressure closings, and a direct line to motivated buyers. FAM changes the math for dealers sitting on inventory.",
  content: (
    <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
      <p>
        Most car dealers have the same problem: inventory they want to move, buyers who need financing, and a gap between the two that banks fill slowly, expensively, and inconsistently. FAM closes that gap — and it does it in a way that's free for dealers to access.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10">A standing offer on every listed vehicle</h2>
      <p>
        When a dealer joins FAM, every vehicle in their listed inventory receives a standing offer. Not a conditional approval. Not a "we'll see when someone applies." A real, structured financing offer backed by FAM's community funding model, ready to activate the moment a creator selects that vehicle for their campaign.
      </p>
      <p>
        The dealer doesn't have to do anything to generate that offer. They list the inventory. The offers exist. If a campaign closes on one of their vehicles, they submit the title and go through FAM's proofing process. That's it.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10">No pressure. No middleman. No games.</h2>
      <p>
        Traditional dealership financing involves a finance manager, a lender relationship, rate markup, and a customer who may or may not qualify by the time they're sitting in the chair. FAM removes that entire chain. The funding is community-sourced and confirmed before the dealer ever gets involved.
      </p>
      <p>
        The offer window is 24 hours. When FAM presents a vehicle offer to a dealer, they have a day to accept — not weeks of back-and-forth. If they accept, the proofing process starts immediately and FAM pays out on completion. Clean and fast.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10">Exposure to a motivated buyer network</h2>
      <p>
        Every campaign creator on FAM is actively looking for a vehicle. They browse dealer inventory through the platform, select up to five vehicles for their campaign, and the best-funded scenario wins. That means dealers listed on FAM get exposure to buyers who are already funded and ready — not tire-kickers running credit checks that go nowhere.
      </p>
      <p>
        The platform also surfaces dealer inventory to FAM's broader investor and gifter community. When a campaign goes live with a specific vehicle selected, everyone following that campaign sees that make, model, and dealer. It's organic visibility with zero cost to the dealer.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10">For hard-to-move inventory specifically</h2>
      <p>
        FAM's model works particularly well for vehicles that sit. Older models, higher mileage, unusual configurations — these are the vehicles that traditional financing struggles with most. FAM's underwriting is community-funded, not bank-underwritten, so the approval criteria look different. A vehicle that a bank won't touch may be exactly what a FAM campaign creator needs — and exactly what a group of funders is willing to back.
      </p>
      <p>
        Dealers who list their full inventory — including vehicles they've had trouble moving — tend to see the most value from the platform. FAM finds a match for cars that the traditional system leaves behind.
      </p>

      <div className="bg-gray-950 text-white rounded-2xl p-6 mt-10">
        <p className="font-bold text-white mb-2">Are you a dealer?</p>
        <p className="text-gray-400 text-sm mb-4">Listing is free. Standing offers are automatic. Get your inventory in front of FAM's community today.</p>
        <a href="/dealers" className="inline-flex items-center gap-1.5 bg-gradient-to-r from-rose-500 to-amber-500 text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:opacity-90 transition-opacity">
          Join as a Dealer <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  ),
};

const ALL_POSTS: Post[] = [POST_1, POST_2, POST_3, POST_4, POST_5];

interface FamNewsProps {
  slug?: string;
}

export function FamNews({ slug }: FamNewsProps) {
  const post = slug ? ALL_POSTS.find(p => p.slug === slug) : null;

  if (post) {
    return <PostView post={post} />;
  }

  return <IndexView />;
}

function IndexView() {
  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gray-950 text-white py-16">
        <div className="container mx-auto px-6 max-w-3xl">
          <span className="text-xs text-rose-400 font-semibold uppercase tracking-widest">FAM News</span>
          <h1 className="text-4xl font-extrabold mt-2 mb-3">FAM News Weekly</h1>
          <p className="text-gray-400 max-w-xl">
            Financing strategies, investing insights, and stories from the FAM community. Published weekly.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-6 max-w-3xl py-12">
        {ALL_POSTS.map(post => (
          <a
            key={post.slug}
            href={`/news/${post.slug}`}
            className="block group border border-gray-100 rounded-2xl p-7 hover:border-rose-200 hover:shadow-md transition-all mb-6"
          >
            <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
              <span className="bg-rose-50 text-rose-600 font-bold px-2 py-0.5 rounded">Issue #{post.issue}</span>
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{post.date}</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{post.readTime}</span>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-rose-600 transition-colors">{post.title}</h2>
            <p className="text-gray-500 text-sm leading-relaxed">{post.subtitle}</p>
            <span className="inline-flex items-center gap-1 text-rose-500 text-sm font-medium mt-4">
              Read post <ArrowRight className="w-4 h-4" />
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}

function PostView({ post }: { post: Post }) {
  return (
    <div className="min-h-screen bg-white">
      <div className="border-b border-gray-100 py-4">
        <div className="container mx-auto px-6 max-w-3xl">
          <a href="/news" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
            <ArrowLeft className="w-4 h-4" /> FAM News Weekly
          </a>
        </div>
      </div>

      <div className="bg-gray-950 text-white py-14">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="flex items-center gap-3 text-xs text-gray-400 mb-4">
            <span className="bg-rose-500/20 text-rose-400 font-bold px-2 py-0.5 rounded">Issue #{post.issue}</span>
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{post.date}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{post.readTime}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-4 leading-tight">{post.title}</h1>
          <p className="text-gray-400 text-lg leading-relaxed max-w-xl">{post.subtitle}</p>
        </div>
      </div>

      <div className="container mx-auto px-6 max-w-3xl py-14">
        {post.content}

        <div className="mt-14 pt-8 border-t border-gray-100 flex justify-between items-center">
          <a href="/news" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
            <ArrowLeft className="w-4 h-4" /> All posts
          </a>
          <a href="/campaigns" className="inline-flex items-center gap-1.5 text-sm text-rose-600 font-medium hover:underline">
            Browse campaigns <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}
